// 🔹 Stylesheets
import '../../../../index.css';
import '../../../../route.css';
import '../../../../reset.css';
import './program-generated.css';

// 🔹 React Core
import React, { FC, useEffect, useRef, useState } from 'react';

// 🔹 UI Component Libraries
import { Tour, message } from 'antd';
import type { TourProps } from 'antd';

// 🔹 Drag & Drop Library
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';

// 🔹 Internal Components
import { ExerciseCard } from './ExerciseCard';
import { ExerciseLibrary } from './ExerciseLibrary';
import { ProgramBuilder } from './ProgramBuilder';
import CommandDialog from 'src/components/Modals/CommandDialog/CommandDialog';

// 🔹 Custom Hooks
import { useProgramStore } from './useProgramStore';
import { useScreenTime } from 'src/hooks/useScreenTime';
import { TRAINER_SECTIONS } from 'src/utils/screenTimeTracker';
import { useLocation, useParams } from 'react-router-dom';
import { programStore, setGeneratedProgram, updateGeneratedProgram } from 'src/store/programStore';
import {
  generateWorkoutProgramCoach,
  type WorkoutProgramParams,
} from 'src/api/mutations/useWorkoutProgram';

const regenerateWorkoutProgram = async (payload: WorkoutProgramParams) => {
  const data = await generateWorkoutProgramCoach(payload);

  if (!data?.program?.data) {
    throw new Error('Workout program response did not include program data.');
  }

  return data.program;
};

export const ProgramGenerated: FC = () => {
  // Use
  const { type } = useParams();
  const location = useLocation();

  // location.state is populated on a fresh generate navigate.
  // It is null when navigating back from /completed (no state was forwarded).
  // The programStore bridges that gap entirely in-memory.
  const { program: storedProgram, payload: storedPayload } = programStore();

  const payload = (location.state?.payload ?? storedPayload) as WorkoutProgramParams | undefined;

  const [program, setProgram] = useState(() => location.state?.program ?? storedProgram);

  const apiProgramData = program?.data;
  const containerRef = useRef<HTMLDivElement>(null);
  const programBuilderRef = useRef<HTMLDivElement>(null);
  const tourPopupRef = useRef<HTMLDivElement>(null);

  // Store
  // skipStoredWeeks = true on a fresh generation so weeks are always rebuilt
  // from the new apiProgramData instead of using cached values from the store.
  // skipStoredWeeks = false on back-navigation so drag-and-drop additions are restored.
  const isFreshNavigation = Boolean(location.state?.program);
  const store = useProgramStore(type, apiProgramData, isFreshNavigation);
  const { reorderLibrary, placeExercise, filteredExercises } = store;

  // State
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [isRemixing, setIsRemixing] = useState(false);
  const [generateErrorVisible, setGenerateErrorVisible] = useState(false);

  // AI Modification states
  const [isAIModifying, setIsAIModifying] = useState(false);
  const [selectedAIOption, setSelectedAIOption] = useState('');
  const [aiModifySuccess, setAiModifySuccess] = useState(false);

  // Keep the programStore in sync on every program change.
  // Covers initial load, remix, and AI modifications – ensures back-navigation
  // from /completed always renders the latest state from the store.
  useEffect(() => {
    if (!program) return;
    if (payload) {
      setGeneratedProgram(program, payload);
    } else {
      updateGeneratedProgram(program);
    }
  }, [program, payload]);

  useEffect(() => {
    if (type === 'ai' && !localStorage.getItem('minipt-tour-done')) {
      setOpen(true);
    }
  }, [type]);

  const handleTourClose = () => {
    setOpen(false);
    localStorage.setItem('minipt-tour-done', 'true');
    document.body.removeAttribute('data-step');
  };

  const handleRemixWorkout = async () => {
    if (isRemixing) return;
    if (!payload) {
      setGenerateErrorVisible(true);
      return;
    }

    setIsRemixing(true);
    try {
      const nextProgram = await regenerateWorkoutProgram(payload);
      setProgram(nextProgram);
      store.resetProgramData(nextProgram.data);
      message.success('Workout remixed successfully');
    } catch {
      setGenerateErrorVisible(true);
    } finally {
      setIsRemixing(false);
    }
  };

  const handleAIModifyWorkout = async (option: string) => {
    if (isAIModifying) return;
    if (!payload) {
      setGenerateErrorVisible(true);
      return;
    }

    setIsAIModifying(true);
    setSelectedAIOption(option);
    setAiModifySuccess(false);

    try {
      const nextProgram = await regenerateWorkoutProgram({
        ...payload,
        preferences: option,
      });
      setProgram(nextProgram);
      store.resetProgramData(nextProgram.data);
      setAiModifySuccess(true);
    } catch {
      setGenerateErrorVisible(true);
    } finally {
      setIsAIModifying(false);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    }),
  );

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const allExs = Object.values(filteredExercises).flat();
    const ex = allExs.find((e) => e._id === active.id);
    if (ex) {
      store.setDraggedExercise(ex);
    }

    // Lock body scroll to prevent jitter on mobile
    document.body.style.overflow = 'hidden';

    // Auto-close library on mobile/small-tablet when dragging starts
    if (window.innerWidth < 1024 && isLibraryOpen) {
      setTimeout(() => {
        setIsLibraryOpen(false);
      }, 100);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    store.setDraggedExercise(null);

    if (window.innerWidth < 1024) {
      setIsLibraryOpen(true);
    }

    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    // source: list-A, destination: slot-week1-Mon-warmup (Place Exercise)
    if (overId.startsWith('slot-')) {
      const allExs = Object.values(filteredExercises).flat();
      const ex = allExs.find((e) => e._id === activeId);
      if (ex) {
        const parts = overId.split('-'); // ["slot", weekId, dayStr, slotType]
        const [, weekId, dayStr, slotType] = parts;

        // Relaxed Validation: Allow drop but show warning if type mismatch
        if (!ex.type.some((t) => t.toLowerCase() === slotType.toLowerCase())) {
          message.warning({
            content: `${ex.name} is ideally for ${ex.type.join('/')}, but I've placed it in ${slotType} for you.`,
            style: { marginTop: '10vh' },
          });
        }

        placeExercise(weekId, dayStr, slotType as any, ex);
      }
      return;
    }

    // Handle Library Reorder
    // In dnd-kit, if dropping on another item in the same list
    const allExs = Object.values(filteredExercises).flat();
    const activeEx = allExs.find((e) => e._id === activeId);
    const overEx = allExs.find((e) => e._id === overId);

    if (activeEx && overEx) {
      const activeLetter = activeEx.name.charAt(0).toUpperCase();
      const overLetter = overEx.name.charAt(0).toUpperCase();

      if (activeLetter === overLetter) {
        const list = filteredExercises[activeLetter];
        const oldIndex = list.findIndex((e) => e._id === activeId);
        const newIndex = list.findIndex((e) => e._id === overId);
        if (oldIndex !== newIndex) {
          reorderLibrary(activeLetter, oldIndex, newIndex);
        }
      }
    }
  };

  // ── Screen time tracking – Section 14 (/trainer/generated/ai) ────────────
  useScreenTime(TRAINER_SECTIONS.SECTION_14);

  const steps: TourProps['steps'] = [
    {
      title: null,
      description:
        "Your client's profile and movement limitations are already loaded. I use these to filter every exercise I suggest.",
      target: () => document.getElementById('builder-header-profile')!,
    },
    {
      title: null,
      description:
        'Explore all our exercises. Use the filters to find exactly what you need for your client.',
      target: () => document.getElementById('exercise-library')!,
    },
    {
      title: null,
      description: 'See your weekly schedule and manage exercises by day.',
      target: () => document.getElementById('week-days-scroll-container')!,
    },
    {
      title: null,
      description: 'Refine sets, reps, and rest directly on each exercise.',
      target: () => document.getElementById('edit-btn')!,
    },
    {
      title: null,
      description: 'Toggle to apply changes across all weeks instantly.',
      target: () => document.getElementById('checkbox-label')!,
    },
    {
      title: null,
      description: 'Quickly remix the entire workout logic.',
      target: () => document.getElementById('remix-btn')!,
    },
    {
      title: null,
      description:
        'The real power is here. Ask me to "Gradually raise workout intensity" or "Convert to superset format" - I\'m here to refine the program with you in real-time.',
      target: () => document.getElementById('ai-btn')!,
    },
  ].filter((step) => {
    if (step.target) {
      const target = typeof step.target === 'function' ? step.target() : step.target;
      return !!target;
    }
    return true;
  });

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="program-generated-container" ref={containerRef}>
        <div ref={tourPopupRef} />

        <CommandDialog
          visible={generateErrorVisible}
          onClose={() => setGenerateErrorVisible(false)}
          type="error"
          title="We couldn’t remix the workout"
          description="Something went wrong while remixing the workout program. Please try again later."
          confirmText="Okay"
        />
        <ExerciseLibrary
          store={store}
          isOpen={isLibraryOpen}
          onClose={() => setIsLibraryOpen(false)}
        />
        <ProgramBuilder
          store={store}
          onOpenLibrary={() => setIsLibraryOpen((prev) => !prev)}
          programBuilderRef={programBuilderRef}
          apiProgramData={apiProgramData}
          payload={payload}
          onRemixWorkout={handleRemixWorkout}
          isRemixing={isRemixing}
          onAIModifyWorkout={handleAIModifyWorkout}
          isAIModifying={isAIModifying}
          selectedAIOption={selectedAIOption}
          aiModifySuccess={aiModifySuccess}
          setAiModifySuccess={setAiModifySuccess}
        />

        {open && (
          <Tour
            getPopupContainer={() =>
              (tourPopupRef.current || containerRef.current || document.body) as any
            }
            open={open}
            onClose={handleTourClose}
            steps={steps}
            rootClassName="minipt-tour"
            indicatorsRender={(current, total) => {
              document.body.setAttribute('data-step', `${current}`);
              return (
                <span className="tour-indicators">
                  {Array.from({ length: total }).map((_, i) => (
                    <span key={i} className={`tour-dot ${i === current ? 'active' : ''}`} />
                  ))}
                </span>
              );
            }}
          />
        )}
      </div>

      <DragOverlay dropAnimation={null}>
        {store.draggedExercise ? (
          <ExerciseCard
            exercise={store.draggedExercise}
            index={0}
            letterKey=""
            isDragging
            isOverlay
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};
